import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { user } from 'services/user';
import { setDockEnabled, setWidgetHidden, setWidgetsOrder, setCountersEnabled } from 'store/actions/widgets';
import { sortWidgetDefs } from './defs';
import { Divider, SectionTitle, Row, Switch, Chevron, List, SelectAll, Item, Handle, Check } from './widgets-menu.styled';

const ChevronIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const GripIcon = () => (
  <svg width="10" height="14" viewBox="0 0 10 16" fill="currentColor" aria-hidden>
    {[2, 8, 14].map((y) => (
      <g key={y}>
        <circle cx="2" cy={y} r="1.4" />
        <circle cx="8" cy={y} r="1.4" />
      </g>
    ))}
  </svg>
);

/**
 * Раздел «Показывать на экране» в меню настроек (CP-дизайн):
 * тумблеры разовых/постоянных клиентов, тумблер панели виджетов и раскрывающийся
 * (по шеврону) список виджетов с видимостью и перетаскиванием для смены порядка.
 */
export const WidgetsMenu = ({ onetime, regular, loadingOnetime, loadingRegular, toggleOnetime, toggleRegular }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const dockEnabled = useSelector((store) => store.widgets.dockEnabled);
  const hiddenWidgets = useSelector((store) => store.widgets.hiddenWidgets, shallowEqual) || [];
  const widgetsOrder = useSelector((store) => store.widgets.widgetsOrder, shallowEqual) || [];
  const countersEnabled = useSelector((store) => store.widgets.countersEnabled !== false);
  const [expanded, setExpanded] = useState(false);
  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);

  const grzEnabled = user.getInterfaceParam('vehicle_plate_control') !== false;
  const defs = sortWidgetDefs(widgetsOrder).filter((w) => !w.grzOnly || grzEnabled);
  const isHidden = (k) => hiddenWidgets.indexOf(k) !== -1;
  const shown = defs.filter((w) => !isHidden(w.key)).length;

  const toggleDock = () => dispatch(setDockEnabled(!dockEnabled));

  const toggleExpanded = (e) => {
    e.stopPropagation();
    setExpanded((v) => !v);
  };

  const showAll = (e) => {
    e.stopPropagation();
    hiddenWidgets.forEach((k) => dispatch(setWidgetHidden(k, false)));
  };

  const resetDrag = () => {
    setDragKey(null);
    setOverKey(null);
  };

  const onDrop = (e, targetKey) => {
    e.preventDefault();
    if (dragKey && dragKey !== targetKey) {
      // порядок хранится по всему реестру (включая скрытые/ГРЗ-виджеты)
      const keys = sortWidgetDefs(widgetsOrder).map((w) => w.key);
      const from = keys.indexOf(dragKey);
      keys.splice(from, 1);
      keys.splice(keys.indexOf(targetKey), 0, dragKey);
      dispatch(setWidgetsOrder(keys));
    }
    resetDrag();
  };

  return (
    <>
      <Divider />
      <SectionTitle>{t('dock.Показывать на экране')}</SectionTitle>

      <Row onClick={() => dispatch(setCountersEnabled(!countersEnabled))}>
        <Switch className={countersEnabled ? 'on' : ''} />
        {t('dock.Счётчики клиентов')}
      </Row>
      <Row className={loadingOnetime ? 'loading' : ''} onClick={toggleOnetime}>
        <Switch className={onetime ? 'on' : ''} />
        {t('widgets.Разовые клиенты')}
      </Row>
      <Row className={loadingRegular ? 'loading' : ''} onClick={toggleRegular}>
        <Switch className={regular ? 'on' : ''} />
        {t('widgets.Постоянные клиенты')}
      </Row>

      <Row className={dockEnabled ? '' : 'off'} onClick={toggleDock}>
        <Switch className={dockEnabled ? 'on' : ''} />
        <span>
          {dockEnabled ? t('dock.Виджеты') : t('dock.Виджеты скрыты')}{' '}
          {dockEnabled && (
            <small>
              {shown} {t('dock.из')} {defs.length}
            </small>
          )}
        </span>
        <Chevron type="button" className={expanded ? 'open' : ''} aria-label={t('dock.Виджеты')} aria-expanded={expanded} onClick={toggleExpanded}>
          <ChevronIcon />
        </Chevron>
      </Row>

      {expanded && (
        <List>
          <SelectAll>
            <button type="button" onClick={showAll}>
              {t('dock.Выбрать все')}
            </button>
          </SelectAll>
          {defs.map((w) => {
            const hidden = isHidden(w.key);
            const cls = [hidden ? 'hidden' : '', dragKey === w.key ? 'dragging' : '', overKey === w.key && dragKey !== w.key ? 'over' : ''].join(' ');
            return (
              <Item
                key={w.key}
                className={cls}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', w.key);
                  setDragKey(w.key);
                }}
                onDragOver={(e) => {
                  if (dragKey) {
                    e.preventDefault();
                    setOverKey(w.key);
                  }
                }}
                onDrop={(e) => onDrop(e, w.key)}
                onDragEnd={resetDrag}
                onClick={() => dispatch(setWidgetHidden(w.key, !hidden))}
              >
                <Handle title={t('dock.Перетащите, чтобы изменить порядок')}>
                  <GripIcon />
                </Handle>
                <Check className={hidden ? '' : 'on'} />
                <span className="label">{t('dock.' + w.label)}</span>
              </Item>
            );
          })}
        </List>
      )}
    </>
  );
};
